/**
 * <SchoolAssessmentAccordion> — a full assessment card that expands to reveal its detail
 * (organism, L0.1). The first DS organism.
 *
 * Composes the generic `<Accordion>` with `<SchoolAssessmentCard>` as the (chevron-flipping)
 * header and `<SchoolAssessmentDetail>` as the content. The shell (raised surface, rounded
 * border, urgency-colored left rail) lives here; the interactive toggle comes from Accordion.
 * Domain-aware tier: takes an `Assessment` and passes it straight down.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Accordion } from "../../molecules/Accordion"
import { SchoolAssessmentCard, URGENCY_VARIANT } from "../../molecules/SchoolAssessmentCard"
import { SchoolAssessmentDetail } from "../../molecules/SchoolAssessmentDetail"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"

export type SchoolAssessmentAccordionProps = {
    assessment: UISchoolAssessment
    defaultExpanded?: boolean
    onEdit?: () => void
    onDelete?: () => void
    onDetails?: () => void
    testID?: string
}

export function SchoolAssessmentAccordion(props: SchoolAssessmentAccordionProps) {
    const { assessment, defaultExpanded = false, onEdit, onDelete, onDetails, testID } = props

    const railColor: SemanticColorKey = URGENCY_VARIANT[assessment.urgency]

    return (
        <View style={styles.shell(railColor)} testID={testID}>
            <Accordion
                defaultExpanded={defaultExpanded}
                header={(expanded) => <SchoolAssessmentCard assessment={assessment} expanded={expanded} />}
            >
                <SchoolAssessmentDetail
                    assessment={assessment}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDetails={onDetails}
                />
            </Accordion>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    shell: (railColor: SemanticColorKey) => ({
        backgroundColor: theme.colors["surface-raised"],
        borderRadius: theme.radius.lg,
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderLeftWidth: 4,
        borderLeftColor: theme.colors[railColor],
        overflow: "hidden" as const,
    }),
}))
